import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField } from "../ReviewPage/FormField";
import { InspectorHeader } from "./InspectorHeader";
import { TraversalControls } from "./TraversalControls";
import { ConfirmActionModal } from "./ConfirmActionModal";
import type {
  BoardItemTypeV3,
  BOQExtractionPosition,
  LvMatch,
} from "./utils/types";

interface BoardPageInspectorV3Props {
  canEdit: boolean;
  itemType: BoardItemTypeV3;
  selectedOccurrenceIndex: number;
  onSelectOccurrence: (index: number) => void;
  isTraversing: boolean;
  onStartTraversing: () => void;
  onStopTraversing: () => void;
  seenIds: Set<string>;
  onGoNextUnseen: () => void;
  onPrevOccurrence: () => void;
  onNextOccurrence: () => void;
  boqPositions?: BOQExtractionPosition[];
  onSaveLvMatch: (lvMatch: LvMatch) => Promise<void>;
  onDeleteItemGroup: () => void;
}

export const BoardPageInspectorV3 = ({
  canEdit,
  itemType,
  selectedOccurrenceIndex,
  onSelectOccurrence,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  seenIds,
  onGoNextUnseen,
  onPrevOccurrence,
  onNextOccurrence,
  boqPositions,
  onSaveLvMatch,
  onDeleteItemGroup,
}: BoardPageInspectorV3Props) => {
  const { t } = useTranslation();

  const [isDeleteGroupConfirmOpen, setIsDeleteGroupConfirmOpen] =
    useState(false);

  const selectedOccurrence =
    itemType.occurrences[selectedOccurrenceIndex] ?? null;

  const occurrenceCount = itemType.occurrences.length;

  const handleRenameGroup = useCallback(async () => {
    // Item types are not renamable - no-op
  }, []);

  const seenCount = useMemo(
    () =>
      itemType.occurrences.filter((occurrence) =>
        seenIds.has(String(occurrence.occurrence_id)),
      ).length,
    [itemType.occurrences, seenIds],
  );

  return (
    <div className="bg-white h-full overflow-hidden flex flex-col">
      <InspectorHeader
        canEdit={canEdit}
        groupName={itemType.item}
        groupTypeOptions={[]}
        onRenameGroup={handleRenameGroup}
        countLabel={t("smartSurveyor.boardPageInspector.pagesTitle")}
        count={occurrenceCount}
        lvMatch={itemType.lvMatch}
        boqPositions={boqPositions}
        onSaveLvMatch={onSaveLvMatch}
        isTraversing={isTraversing}
        onStartTraversing={onStartTraversing}
        onStopTraversing={onStopTraversing}
        isGroupNameEditable={false}
        onDeleteGroup={() => setIsDeleteGroupConfirmOpen(true)}
      />
      {isTraversing && (
        <div className="overflow-y-auto flex flex-col min-h-0 w-full flex-1 px-4 py-3 gap-3">
          {occurrenceCount > 0 && (
            <TraversalControls
              editingLabel={t(
                "smartSurveyor.boardPageInspector.reviewingOccurrence",
              )}
              selectedIndex={selectedOccurrenceIndex}
              total={occurrenceCount}
              seenCount={seenCount}
              onPrev={onPrevOccurrence}
              onNext={onNextOccurrence}
              onSelectIndex={onSelectOccurrence}
              onNextUnseen={onGoNextUnseen}
            />
          )}
          {selectedOccurrence && (
            <div className="text-sm text-gray-700 space-y-3">
              <FormField
                value={selectedOccurrence.count}
                label={t("smartSurveyor.boardPageInspector.countLabel")}
                type="number"
              />
              {selectedOccurrence.circuits.length > 0 && (
                <FormField
                  value={selectedOccurrence.circuits.join(", ")}
                  label={t("smartSurveyor.boardPageInspector.circuitsLabel")}
                  type="input"
                />
              )}
            </div>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={isDeleteGroupConfirmOpen}
        onClose={() => setIsDeleteGroupConfirmOpen(false)}
        onConfirm={() => onDeleteItemGroup()}
        action="delete"
        title={t("smartSurveyor.groupInspector.deleteGroupModal.title")}
        description={t(
          "smartSurveyor.groupInspector.deleteGroupModal.itemDescription",
        )}
      />
    </div>
  );
};
